
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../../services/api';
import { useCurrency } from '../CurrencyContext';
import { CURRENCIES } from '../../constants';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';
import { MRRMovement } from '../../types';

const MRRMovementChart: React.FC = () => {
  const { currencyCode, currencySymbol } = useCurrency();
  const [range, setRange] = useState<DateRangeOption>('30d');
  const [data, setData] = useState<MRRMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const activeRate = CURRENCIES.find(c => c.code === currencyCode)?.rate || 1;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const mrrData = await api.analytics.getMrrMovement();
        setData(mrrData || []);
      } catch (error) {
        console.error("Failed to fetch MRR data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range]);

  if (loading) {
    return (
      <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm h-[520px] flex items-center justify-center">
        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h2 className="text-xl font-black text-slate-900 mb-1">MRR Movement</h2>
          <p className="text-sm text-slate-500">Breakdown of monthly recurring revenue by source.</p>
        </div>
        <ReportDateSelector value={range} onChange={setRange} />
      </div>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} stackOffset="sign">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{fontSize: 12, fill: '#64748b'}} 
              tickFormatter={(v) => `${currencySymbol}${Math.floor((v * activeRate)/1000)}k`} 
            />
            <Tooltip 
              cursor={{fill: '#f8fafc'}}
              contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
              formatter={(v: number) => [`${currencySymbol}${(v * activeRate).toLocaleString()}`, '']}
            />
            <Legend verticalAlign="top" align="right" iconType="circle" />
            <Bar dataKey="new" name="New MRR" stackId="a" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expansion" name="Expansion" stackId="a" fill="#10b981" />
            <Bar dataKey="contraction" name="Contraction" stackId="a" fill="#f59e0b" />
            <Bar dataKey="churn" name="Churned" stackId="a" fill="#ef4444" radius={[0, 0, 4, 4]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MRRMovementChart;
